/**
 * ImageSlotModal — the URL entry path for product images (V3: extracted from
 * ProductFormWizard so the detail Images section reuses the SAME modal —
 * zodResolver on `imageSlotRowSchema`, 44px buttons, house field styling).
 */

import { useEffect, useMemo } from 'react';
import { Input, Modal, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { imageSlotRowSchema } from '@/schemas/forms/productFormSchemas';
import type { ImageSlotRow } from '@/schemas/forms/productFormSchemas';

const { Text } = Typography;

export interface ImageSlotModalProps {
  open: boolean;
  onClose: () => void;
  initial: ImageSlotRow | null;
  nextSlot: number;
  onSave: (row: ImageSlotRow) => void;
}

export const ImageSlotModal = ({
  open,
  initial,
  nextSlot,
  onClose,
  onSave,
}: ImageSlotModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const isEdit = initial !== null;

  const emptyRow = useMemo<ImageSlotRow>(
    () => ({ slot: nextSlot, url: '', caption: '' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, nextSlot]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ImageSlotRow>({
    resolver: zodResolver(imageSlotRowSchema),
    defaultValues: emptyRow,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(initial ?? emptyRow);
  }, [open, initial, emptyRow, reset]);

  return (
    <Modal
      open={open}
      title={isEdit ? t('products:wizard.modals.editImage') : t('products:wizard.modals.addImage')}
      okText={isEdit ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={handleSubmit((row) => {
        onSave(row);
        onClose();
      })}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <div style={formStyles.fieldContainerStyle}>
        <Text style={formStyles.labelStyle}>
          <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
          {t('products:fields.imageUrl')}
        </Text>
        <Controller
          name="url"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              style={formStyles.inputStyle}
              status={errors.url ? 'error' : undefined}
            />
          )}
        />
        {errors.url && <Text style={formStyles.errorStyle}>{t(errors.url.message ?? '')}</Text>}

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {t('products:fields.imageCaption')}
        </Text>
        <Controller
          name="caption"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              size="large"
              style={formStyles.inputStyle}
            />
          )}
        />
      </div>
    </Modal>
  );
};

export default ImageSlotModal;
